import { FormEvent, useEffect, useMemo, useState } from 'react';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from "../catalog/catalogAlerts";
import {
    emptySubscriberForm,
    extractApiErrorMessage,
    extractSubscriberCollection,
    formatDateTime,
    SubscriberRecord,
    subscriberToFormState,
    toBoolean,
    validateSubscriberForm,
} from "./communicationsApi";

const inputClassName = "w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white";

export default function CommunicationsSubscribers() {
    const [subscribers, setSubscribers] = useState<SubscriberRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSubscriber, setEditingSubscriber] = useState<SubscriberRecord | null>(null);
    const [formData, setFormData] = useState(emptySubscriberForm);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const totalItems = subscribers.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const visibleSubscribers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return subscribers.slice(startIndex, startIndex + rowsPerPage);
    }, [currentPage, rowsPerPage, subscribers]);

    useEffect(() => {
        void refreshData();
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const refreshData = async (options?: { focusId?: number; targetPage?: number; }) => {
        setLoading(true);
        try {
            const response = await client.getApiCommunicationsSubscribers() as any;
            const nextSubscribers = extractSubscriberCollection(response);
            setSubscribers(nextSubscribers);

            if (options?.focusId) {
                const focusedIndex = nextSubscribers.findIndex((subscriber) => subscriber.id === options.focusId);

                if (focusedIndex >= 0) {
                    setCurrentPage(Math.floor(focusedIndex / rowsPerPage) + 1);
                    return;
                }
            }

            if (options?.targetPage) {
                const nextTotalPages = Math.max(1, Math.ceil(nextSubscribers.length / rowsPerPage));
                setCurrentPage(Math.min(options.targetPage, nextTotalPages));
            }
        } catch (err) {
            console.error('Failed to fetch subscribers:', err);
            setSubscribers([]);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to load subscribers'));
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingSubscriber(null);
        setFormData(emptySubscriberForm());
    };

    const openCreateModal = () => {
        setEditingSubscriber(null);
        setFormData(emptySubscriberForm());
        setIsModalOpen(true);
    };

    const openEditModal = (subscriber: SubscriberRecord) => {
        setEditingSubscriber(subscriber);
        setFormData(subscriberToFormState(subscriber));
        setIsModalOpen(true);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const validated = validateSubscriberForm(formData);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            if (editingSubscriber) {
                await client.postApiCommunicationsSubscriberUpdate({
                    postApiCommunicationsSubscriberUpdateRequest: {
                        id: editingSubscriber.id,
                        ...validated.data,
                    }
                });

                closeModal();
                await refreshData({ focusId: editingSubscriber.id });
                showSuccessToast('Subscriber updated successfully');
            } else {
                const response = await client.postApiCommunicationsSubscriberCreate({
                    postApiCommunicationsSubscriberCreateRequest: validated.data
                }) as any;

                const createdId = Number(response?.data?.data?.id ?? response?.data?.id);

                closeModal();
                await refreshData(Number.isFinite(createdId) ? { focusId: createdId } : undefined);
                showSuccessToast('Subscriber created successfully');
            }
        } catch (err) {
            console.error('Failed to save subscriber:', err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to save subscriber'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (subscriber: SubscriberRecord) => {
        const isConfirmed = await confirmAction({
            text: 'This subscriber will be permanently deleted.',
            confirmButtonText: 'Delete Subscriber'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCommunicationsSubscriberDelete({
                postApiBillingBillingDetailsDeleteRequest: { id: subscriber.id }
            } as any);

            if (editingSubscriber?.id === subscriber.id) {
                closeModal();
            }

            const nextPage = visibleSubscribers.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            await refreshData({ targetPage: nextPage });
            showSuccessToast('Subscriber deleted successfully');
        } catch (err) {
            console.error('Failed to delete subscriber:', err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to delete subscriber'));
        }
    };

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 p-4 dark:border-gray-800">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Subscribers</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {loading ? 'Loading subscribers...' : `${totalItems} subscriber${totalItems === 1 ? '' : 's'} loaded.`}
                    </p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
                >
                    Add Subscriber
                </button>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Email</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Updated</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    Loading subscribers...
                                </TableCell>
                            </TableRow>
                        ) : visibleSubscribers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No subscribers found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleSubscribers.map((subscriber) => (
                                <TableRow key={subscriber.id}>
                                    <TableCell className="px-5 py-4">
                                        <div className="space-y-1">
                                            <div className="font-medium text-gray-800 dark:text-white">{subscriber.email}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                Created {formatDateTime(subscriber.created_at)}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <Badge color={toBoolean(subscriber.active) ? 'success' : 'light'} size="sm">
                                            {toBoolean(subscriber.active) ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(subscriber.updated_at || subscriber.created_at)}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => openEditModal(subscriber)}
                                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-brand-200 hover:text-brand-500 dark:border-gray-700 dark:text-gray-300"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => void handleDelete(subscriber)}
                                                className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-900/10"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {!loading && totalItems > 0 ? (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(rows) => {
                        setRowsPerPage(rows);
                        setCurrentPage(1);
                    }}
                    totalItems={totalItems}
                />
            ) : null}

            <Modal isOpen={isModalOpen} onClose={closeModal} className="max-w-xl">
                <div className="flex max-h-[80vh] flex-col overflow-hidden">
                    <div className="shrink-0 border-b border-gray-200 px-6 py-5 pr-16 dark:border-gray-800 sm:px-8">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {editingSubscriber ? 'Edit Subscriber' : 'Add Subscriber'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Frontend validation mirrors the current API rules for subscribers.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(event) => setFormData((previous) => ({ ...previous, email: event.target.value }))}
                                        className={inputClassName}
                                        placeholder="subscriber@example.com"
                                        required
                                        disabled={submitting}
                                    />
                                </div>

                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Status
                                    </label>
                                    <select
                                        value={formData.active ? 'true' : 'false'}
                                        onChange={(event) => setFormData((previous) => ({ ...previous, active: event.target.value === 'true' }))}
                                        className={inputClassName}
                                        disabled={submitting}
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900 sm:px-8">
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Saving...' : editingSubscriber ? 'Update Subscriber' : 'Add Subscriber'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
